import { useState } from 'react'

import {
  ChevronDownIcon,
  ChevronRightIcon,
  Collapsible,
  RadioGroup,
  Table,
  Text,
  UnstyledButton,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import styles from './LinkedInVerifiedSkills.module.css'
import { SkillGroup } from './SkillGroup'
import type { LinkedInVerifiedCapability } from './config'

type SkillsTableProps = {
  capabilities: LinkedInVerifiedCapability[]
  isSelectionDisabled: boolean
  selectedCapabilityKey?: LinkedInVerifiedCapability['capabilityKey']
  chooseCapabilityPopoverCapabilityKey?: LinkedInVerifiedCapability['capabilityKey']
  isChooseCapabilityPopoverOpen: boolean
  shouldShowChooseCapabilityPopover: boolean
  onChooseCapabilityPopoverOpenChange: (isOpen: boolean) => void
  onCapabilitySelect: (
    capabilityKey: LinkedInVerifiedCapability['capabilityKey'],
  ) => void
  onTableToggle: (expanded: boolean) => void
}

const SkillsTable = ({
  capabilities,
  isSelectionDisabled,
  selectedCapabilityKey,
  chooseCapabilityPopoverCapabilityKey,
  isChooseCapabilityPopoverOpen,
  shouldShowChooseCapabilityPopover,
  onChooseCapabilityPopoverOpenChange,
  onCapabilitySelect,
  onTableToggle,
}: SkillsTableProps): React.JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false)
  const earnedCapabilities = capabilities.filter(
    (capability) => capability.isEarned,
  )
  const notEarnedCapabilities = capabilities.filter(
    (capability) => !capability.isEarned,
  )
  const hasEarnedCapabilities = earnedCapabilities.length > 0
  const summaryText = `${earnedCapabilities.length} of ${capabilities.length} capabilities available to show`

  const handleCapabilityChange = (capabilityKey: string): void => {
    const selectedCapability = earnedCapabilities.find(
      (capability) => capability.capabilityKey === capabilityKey,
    )

    if (!selectedCapability) {
      return
    }

    onCapabilitySelect(selectedCapability.capabilityKey)
  }
  const handleOpenChange = (expanded: boolean): void => {
    setIsExpanded(expanded)
    onTableToggle(expanded)
  }

  return (
    <Collapsible open={isExpanded} onOpenChange={handleOpenChange}>
      <div className={styles.summaryRow}>
        <Collapsible.Trigger asChild>
          <UnstyledButton
            className={styles.summaryTrigger}
            data-testid="linkedin-verified-skills-toggle"
          >
            {isExpanded ? (
              <ChevronDownIcon size="small" color="subtle" />
            ) : (
              <ChevronRightIcon size="small" color="subtle" />
            )}
            <Text weight="medium" className={styles.summaryText}>
              {summaryText}
            </Text>
          </UnstyledButton>
        </Collapsible.Trigger>
      </div>
      <Collapsible.Content>
        <RadioGroup
          disabled={isSelectionDisabled}
          label={
            <VisuallyHidden>
              Capability to show on your LinkedIn profile
            </VisuallyHidden>
          }
          onChange={handleCapabilityChange}
          value={selectedCapabilityKey}
        >
          <Table fullWidth className={styles.table}>
            <Table.Header className={styles.tableHeader}>
              <Table.Row>
                <Table.HeaderCell>Capabilities</Table.HeaderCell>
                <Table.HeaderCell>How to achieve</Table.HeaderCell>
                {hasEarnedCapabilities ? (
                  <Table.HeaderCell
                    align="center"
                    className={styles.showHeaderCell}
                  >
                    Show
                  </Table.HeaderCell>
                ) : null}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {hasEarnedCapabilities ? (
                <SkillGroup
                  label={`Achieved (${earnedCapabilities.length})`}
                  capabilities={earnedCapabilities}
                  isEarned
                  isSelectionDisabled={isSelectionDisabled}
                  shouldShowSelectionColumn={hasEarnedCapabilities}
                  chooseCapabilityPopoverCapabilityKey={
                    chooseCapabilityPopoverCapabilityKey
                  }
                  isChooseCapabilityPopoverOpen={isChooseCapabilityPopoverOpen}
                  shouldShowChooseCapabilityPopover={
                    shouldShowChooseCapabilityPopover
                  }
                  onChooseCapabilityPopoverOpenChange={
                    onChooseCapabilityPopoverOpenChange
                  }
                />
              ) : null}
              <SkillGroup
                label={`Not achieved (${notEarnedCapabilities.length})`}
                capabilities={notEarnedCapabilities}
                isEarned={false}
                isSelectionDisabled={isSelectionDisabled}
                shouldShowSelectionColumn={hasEarnedCapabilities}
                isChooseCapabilityPopoverOpen={false}
                shouldShowChooseCapabilityPopover={false}
                onChooseCapabilityPopoverOpenChange={
                  onChooseCapabilityPopoverOpenChange
                }
              />
            </Table.Body>
          </Table>
        </RadioGroup>
      </Collapsible.Content>
    </Collapsible>
  )
}

export { SkillsTable }
